/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/require-default-props */
/* eslint-disable no-await-in-loop */
import {
  buildStoryVideoContext,
  buildTemplateChange,
} from '@src/analytics/eventPayloadHelpers';
import * as EventTracker from '@src/analytics/EventTracker';
import { useTopicFilter } from '@src/hooks/useTopicFilter';
import { useMonitorClipProduction } from '@src/monitoring/hooks';
import {
  useStore,
  useTranscription,
  useUserIdentity,
} from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { transcriptEndSeconds } from '@src/utility/clipMediaSource';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import { observer } from 'mobx-react-lite';
import { memo, useEffect, useMemo, useRef, useState } from 'react';

import { AIClipProcessor } from '../../services/AIClipProcessor';
import {
  analyzeParamsForTheLackOfClips,
  produceClipsChunked,
} from '../../services/AIClipProducer';
import { clipProcessorDepsFromStore } from '../../services/clipProcessorDepsFromStore';
import { extractErrorInfo } from '../../utility/error';
import { LogManager } from '../../utility/Logger';
import { parseClipThemeTitles } from '../../utility/story';
import { useFlagsCombination } from '../../utility/useFlagsCombination';
import { useAIClipProducerTheme } from './useAIClipProducerTheme';

import type { TranscriptElement } from '../../types.ts/transcript';
import type { AudienceOption } from '../common/AudienceSelector';
import type { GoalOption } from '../common/GoalSelector';
import type { Speaker } from '../content-components/SpeakerTypes';
import type { Dispatch, FunctionComponent, SetStateAction } from 'react';

const logger = LogManager.getLogger('AIClipProducer.tsx');

type ClipProducerProps = {
  ClipProducerUI: FunctionComponent<ClipProducerUIProps>;
  useFinalOrOriginalVideo?: boolean;
  supportedPresets:
    | {
        title: string;
        category: string;
        userPromptDescription: string;
      }[]
    | null;
  enableSpeakerSelection?: boolean; // Added to control speaker selection feature
};

export type ClipProducerUIProps = {
  supportedPresets: {
    title: string;
    category: string;
    userPromptDescription: string;
  }[];
  isProducing: boolean;
  retryCount: number;
  onProductionCancel: () => void;
  selectedDuration: number | null;
  setSelectedDuration: (duration: number | null) => void;
  selectedPresets: string[];
  setSelectedPresets: (presets: string[]) => void;
  onPresetClick: (preset: string) => void;
  customPresetText: string;
  setCustomPresetText: (text: string) => void;
  selectedSocials: string[];
  setSelectedSocials: (socials: string[]) => void;
  producedClips: any[] | null;
  showModal: boolean;
  isProducingDisable: boolean;
  onProduceButtonClick: () => void;
  closeProducingModal: () => void;
  // Suggested topics (clip themes) props
  suggestedThemes: string[];
  selectedSuggestedTheme: string | null;
  onSuggestedThemeClick: (title: string) => void;
  // New speaker selection props
  speakers: Speaker[];
  selectedSpeakers: Speaker[];
  setSelectedSpeakers: (speakers: Speaker[]) => void;
  useSpeakerFilter: boolean;
  setUseSpeakerFilter: (useFilter: boolean) => void;
  isLoadingSpeakers: boolean;
  showSpeakerSelector: boolean;
  // Goal selection props
  selectedGoal: GoalOption | null;
  setSelectedGoal: (goal: GoalOption | null) => void;
  useGoalFilter: boolean;
  setUseGoalFilter: Dispatch<SetStateAction<boolean>>;
  // Audience selection props
  selectedAudiences: AudienceOption[];
  setSelectedAudiences: (audiences: AudienceOption[]) => void;
  useAudienceFilter: boolean;
  setUseAudienceFilter: Dispatch<SetStateAction<boolean>>;
  // Video Template selection
  selectedVideoTemplateId: string | undefined;
  setSelectedVideoTemplateId: (templateId: string | undefined) => void;
  useVideoTemplateFilter: boolean;
  setUseVideoTemplateFilter: Dispatch<SetStateAction<boolean>>;
  // Topic filter selection
  storyTopics: string[];
  selectedTopics: string[];
  handleTopicsApply: (topics: string[]) => void;
  // Feature flag for custom preset input
  enableCustomPreset: boolean;
};

const AIClipProducer = memo(
  // observer so the component reacts to MobX store changes (notably
  // videoCreator.story.clipThemesJson): when opened via Home → Produce Clips
  // the producer auto-opens while the story is still loading, and without
  // observer the topics dropdown would never appear once clipThemesJson arrives.
  observer((props: ClipProducerProps) => {
    const videoCreator = useVideoCreatorStore();
    const rootStore = useStore();
    const transcriptionStore = useTranscription();
    const {
      ClipProducerUI,
      useFinalOrOriginalVideo = false,
      enableSpeakerSelection = false,
    } = props;
    const userIdentity = useUserIdentity();
    const {
      enableParallelClipSave,
      contentStudioClipProducerEnableCustom,
      contentStudioClipProducerEnableTextVerification,
      contentStudioClipProducerFilterTranscriptBySpeaker,
      contentStudioClipProducerChunkSize,
      enableClipProducerVideoTemplateSelection,
    } = useFlagsCombination(userIdentity.currentRole);
    const {
      selectedPresets,
      setSelectedPresets,
      onPresetClick,
      suggestedThemes,
      selectedSuggestedTheme,
      onSuggestedThemeClick,
      speakers,
      selectedSpeakers,
      setSelectedSpeakers,
      useSpeakerFilter,
      setUseSpeakerFilter,
      isLoadingSpeakers,
      showSpeakerSelector,
    } = useAIClipProducerTheme({ enableSpeakerSelection });
    const [selectedSocials, setSelectedSocials] = useState<string[]>([]);
    const [selectedDuration, setSelectedDuration] = useState<number | null>(
      null,
    );
    const [isProducing, setIsProducing] = useState(false);
    const [producedClips, setProducedClips] = useState<any[] | null>(null);
    const producerAbortController = useRef<{
      id: string;
      aborted: boolean;
    } | null>(null);
    const [showModal, setShowModal] = useState(false);

    const [selectedGoal, setSelectedGoal] = useState<GoalOption | null>(null);
    const [useGoalFilter, setUseGoalFilter] = useState(false);
    const [selectedAudiences, setSelectedAudiences] = useState<
      AudienceOption[]
    >([]);
    const [useAudienceFilter, setUseAudienceFilter] = useState(false);
    const [selectedVideoTemplateId, _setSelectedVideoTemplateId] = useState<
      string | undefined
    >();
    const [useVideoTemplateFilter, _setUseVideoTemplateFilter] =
      useState(false);

    const previousAppliedTemplateRef = useRef<{
      id?: string;
      name?: string;
    }>({});

    const resolveTemplateName = (id?: string): string | undefined =>
      id
        ? videoCreator.organization?.customerType?.videoTemplates?.find(
            t => t.id === id,
          )?.label
        : undefined;

    const fireVideoTemplateApplied = (newId: string | undefined) => {
      if (!newId) return;
      const newName = resolveTemplateName(newId);
      EventTracker.trackVideoTemplateApplied({
        action_source: 'produce_modal',
        ...buildStoryVideoContext(videoCreator),
        ...buildTemplateChange(previousAppliedTemplateRef.current, {
          id: newId,
          name: newName,
        }),
      });
      previousAppliedTemplateRef.current = { id: newId, name: newName };
    };

    const setSelectedVideoTemplateId = (templateId: string | undefined) => {
      _setSelectedVideoTemplateId(templateId);
      if (useVideoTemplateFilter) fireVideoTemplateApplied(templateId);
    };

    const setUseVideoTemplateFilter: Dispatch<
      SetStateAction<boolean>
    > = value => {
      _setUseVideoTemplateFilter(prev => {
        const next = typeof value === 'function' ? value(prev) : value;
        if (next && !prev) fireVideoTemplateApplied(selectedVideoTemplateId);
        return next;
      });
    };

    const storyTopics = useMemo(
      () => parseClipThemeTitles(videoCreator.story?.clipThemesJson),
      [videoCreator.story?.clipThemesJson],
    );

    // Track clip producer opened
    useEffect(() => {
      EventTracker.trackClipProducerOpened({
        story_id: videoCreator.story?.id,
        video_id: videoCreator.currentVideo?.id,
      });
    }, []);

    // Auto-enable video template filter when org has a default template.
    // Uses the raw setters so this org-default seed does NOT fire
    // video_template_applied — that event is reserved for user actions.
    useEffect(() => {
      const orgDefaultId = videoCreator.organization?.defaultVideoTemplate?.id;
      if (orgDefaultId && enableClipProducerVideoTemplateSelection) {
        _setSelectedVideoTemplateId(orgDefaultId);
        _setUseVideoTemplateFilter(true);
        previousAppliedTemplateRef.current = {
          id: orgDefaultId,
          name: resolveTemplateName(orgDefaultId),
        };
      }
    }, [videoCreator.organization?.defaultVideoTemplate?.id]);

    const MAX_RETRIES = 3;

    const [presetInputValue, setPresetInputValue] = useState<string>('');

    const { selectedTopics, handleTopicsApply } = useTopicFilter(
      presetInputValue,
      setPresetInputValue,
    );

    const [retryCount, setRetryCount] = useState<number>(0);
    const retryCountRef = useRef(retryCount);
    retryCountRef.current = retryCount;
    // The production loop polls this ref between awaits. Bump it directly:
    // a run that fails before ever yielding to a render (an immediate empty
    // result or a synchronous throw) would otherwise re-enter the loop on a
    // microtask with the ref still at 0 and spin the tab forever.
    const bumpRetryCount = () => {
      retryCountRef.current += 1;
      setRetryCount(retryCountRef.current);
    };

    // Track completion to prevent multiple completion calls
    const hasCompletedRef = useRef(false);
    const monitoring = useMonitorClipProduction({
      enabled: true,
      includeDetailedMetadata: true,
      trackPerformance: true,
    });

    const handleProductionCancel = () => {
      if (producerAbortController.current && isProducing) {
        producerAbortController.current.aborted = true;
        producerAbortController.current.id = '<aborted>';
        monitoring.cancelProduction('User cancelled clip production');
      }
      setRetryCount(0);
      setIsProducing(false);
      setProducedClips(null);
      setShowModal(false);
    };

    const closeProducingModal = () => {
      if (producerAbortController.current && isProducing) {
        producerAbortController.current.aborted = true;
        monitoring.cancelProduction('User cancelled clip production');
      }
      setProducedClips(null);
      setIsProducing(false);
      setShowModal(false);
    };

    const isProducingDisabled = () =>
      isProducing || !videoCreator.story?.originalVideo;

    const onProduceButtonClick = async () => {
      if (
        !(selectedPresets.length || presetInputValue || selectedSuggestedTheme)
      ) {
        return;
      }
      // PLAT-11445: production is a transcript action; without a ready
      // transcript there is nothing to pick from and the clip-count math
      // divides by the transcript-derived duration (0 → Infinity → hang).
      // The producer buttons gate on this too — this is the backstop.
      if (
        !transcriptionStore.originalTranscription?.elements?.length ||
        (transcriptEndSeconds(
          transcriptionStore.originalTranscription?.elements,
        ) ?? 0) <= 0
      ) {
        videoCreator.showToast({
          state: 'warning',
          message: 'Transcript is still processing',
          delay: 5000,
        });
        return;
      }

      // Start clip production tracking
      const trackingMetadata = {
        theme: selectedPresets[0] || 'Custom',
        customTheme: selectedSuggestedTheme || presetInputValue || undefined,
        platform: selectedSocials[0] || undefined,
        duration: selectedDuration,
        useSpeakerFilter,
        selectedSpeakersCount: selectedSpeakers.length,
        useGoalFilter,
        selectedGoal: selectedGoal?.id,
        useAudienceFilter,
        selectedAudiencesCount: selectedAudiences.length,
        storyId: videoCreator.story?.id,
        videoId: videoCreator.currentVideo?.id,
      };

      // Reset completion tracking for new production
      hasCompletedRef.current = false;
      monitoring.startProduction(trackingMetadata);

      EventTracker.trackClipGenerationStarted({
        ...buildStoryVideoContext(videoCreator),
        theme: selectedPresets[0] || 'Custom',
        custom_prompt: selectedSuggestedTheme || presetInputValue || undefined,
        platform: selectedSocials[0] || undefined,
        duration: selectedDuration,
        speaker_count: selectedSpeakers.length,
        goal: selectedGoal?.id,
        audience_count: selectedAudiences.length,
        template_applied: useVideoTemplateFilter,
        template_id: selectedVideoTemplateId,
        template_name: resolveTemplateName(selectedVideoTemplateId),
        selected_topics: selectedTopics,
      });

      setIsProducing(true);
      setShowModal(true);
      producerAbortController.current = {
        id: Date.now().toString(),
        aborted: false,
      };

      // PLAT-11445 (audio-first): clip production is a transcript action — the
      // AI picks transcript ranges; the video is only needed for preview
      // (local stream now, 270p once ready) and publish. While renditions are
      // pending, skip the video-editor build (createNewVideoFromSource +
      // newVideoEditor below): it hangs the tab on a not-yet-processed
      // audio-first video, and production doesn't need it. Duration comes from
      // the transcript, which already spans the recording.
      const renditionPending = isCompressedRenditionPending(videoCreator.story);

      const finalVideo = videoCreator.story?.finalVideo;

      if (renditionPending) {
        videoCreator.duration =
          transcriptEndSeconds(
            transcriptionStore.originalTranscription?.elements,
          ) ?? 0;
      } else if (
        useFinalOrOriginalVideo &&
        finalVideo?.id &&
        finalVideo.videoFilePrimary?.video
      ) {
        await videoCreator.loadVideoWithoutRendering(finalVideo.id, true);
        videoCreator.duration = finalVideo.videoFilePrimary!.video.duration;
      } else if (useFinalOrOriginalVideo) {
        // Production setup, not a clip open: keep the usable timer out of it.
        await videoCreator.createNewVideoFromSource(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          false,
        );
        const probedDuration = parseFloat(videoCreator.originalVideoDuration);
        videoCreator.duration =
          probedDuration > 0
            ? probedDuration
            : (transcriptEndSeconds(
                transcriptionStore.originalTranscription?.elements,
              ) ?? 0);
      }

      // Get original transcript elements
      const originalElements = videoCreator.currentVideo?.videoFilePrimary
        ? transcriptionStore.getFinalTranscriptionElements()
        : transcriptionStore.originalTranscription?.elements || [];

      // Always send the full transcript to the AI for better context.
      // Speaker preference is communicated via speakerContext in the prompt.
      let transcriptElements = originalElements;
      const transcriptionText =
        transcriptionStore.getTranscriptionText(originalElements);
      const submitTranscriptionText = transcriptionText;

      if (
        enableParallelClipSave &&
        videoCreator.currentVideo &&
        !renditionPending
      ) {
        // This is needed so that the produced clips' transcriptPosition is
        // predictable when saving clips in parallel. Skipped while renditions
        // are pending — building a VideoEditor over the not-yet-processed
        // audio-first video is the path that hangs; production reads the
        // transcript straight from the store instead.
        try {
          const editor = AIClipProcessor.newVideoEditor(
            videoCreator.currentVideo,
            clipProcessorDepsFromStore(videoCreator),
          );
          if (editor.getFullTranscript().length > 0) {
            transcriptElements =
              editor.getFullTranscript() as TranscriptElement[];
          }
        } catch (e) {
          // AIClipProcessor will log errors
        }
      }

      while (retryCountRef.current < MAX_RETRIES) {
        try {
          const abortTokenId = producerAbortController.current.id;

          const matchedParts = await produceClipsChunked({
            videoCreator,
            originalVideo:
              videoCreator.currentVideo?.videoFilePrimary ||
              videoCreator.story!.originalVideo!,
            submitTranscriptionText,
            transcriptionText,
            transcriptionElements: transcriptElements,
            originalDuration: videoCreator.duration,
            duration: selectedDuration,
            theme: selectedPresets[0] || 'Custom',
            customTheme:
              selectedSuggestedTheme || presetInputValue || undefined,
            extraClipMetadata: {
              ...(presetInputValue && { customPrompt: presetInputValue }),
              ...(selectedSuggestedTheme && {
                suggestedTheme: selectedSuggestedTheme,
              }),
            },
            themeDisplayName: selectedSuggestedTheme || undefined,
            platform: selectedSocials[0] || undefined,
            onClipsProduced: (clips: any) => {
              setProducedClips(prevClips =>
                clips.length !== prevClips?.length ? [...clips] : prevClips,
              );

              // Complete workflow when first clips are produced and shown
              if (
                clips.length > 0 &&
                monitoring.isEnabled &&
                !hasCompletedRef.current
              ) {
                hasCompletedRef.current = true; // Prevent multiple completions
                monitoring.completeProduction(
                  clips.length,
                  retryCountRef.current,
                  {
                    finalTheme: selectedPresets[0] || 'Custom',
                    finalPlatform: selectedSocials[0] || undefined,
                  },
                );
                EventTracker.trackClipGenerationCompleted({
                  story_id: videoCreator.story?.id,
                  story_title: videoCreator.story?.title,
                  video_id: videoCreator.currentVideo?.id,
                  clips_count: clips.length,
                  retry_count: retryCountRef.current,
                  theme: selectedPresets[0] || 'Custom',
                  platform: selectedSocials[0] || undefined,
                });
              }
            },
            abortToken: producerAbortController.current,
            rootStore,
            speakers: useSpeakerFilter ? selectedSpeakers : [], // Pass selected speakers for name lookup
            allSpeakers: speakers, // All speakers for transcript annotation
            goal: useGoalFilter ? selectedGoal : null, // Pass selected goal for context
            audiences: useAudienceFilter ? selectedAudiences : [], // Pass selected audiences
            videoTemplateId: useVideoTemplateFilter
              ? selectedVideoTemplateId
              : undefined, // Pass selected video template ID
            config: {
              enableTextVerification:
                contentStudioClipProducerEnableTextVerification,
              enableSpeakerFiltering:
                contentStudioClipProducerFilterTranscriptBySpeaker,
              chunkSize: contentStudioClipProducerChunkSize,
            },
          });

          if (abortTokenId !== producerAbortController.current.id) {
            logger.info(
              'Discarding results from the aborted run',
              matchedParts,
            );
            return;
          }

          logger.info('ALL PRODUCED CLIPS', matchedParts);
          if (!matchedParts?.length) {
            // The first failure - display ui notification about what the problem might be
            if (retryCountRef.current === 0) {
              const paramsToAnalyze = {
                durations: {
                  originalDuration: videoCreator.duration,
                  selectedDuration: selectedDuration,
                },
                aborted: producerAbortController.current.aborted,
                speakers: useSpeakerFilter ? selectedSpeakers : [],
                goal: useGoalFilter ? selectedGoal : null,
                audiences: useAudienceFilter ? selectedAudiences : [],
              };
              const result = analyzeParamsForTheLackOfClips(paramsToAnalyze);
              if (result) {
                logger.warn(
                  'Found a reason for clip production failure, notifying user',
                  result,
                );
                notifyUserAboutTheLackOfClips(result);
              } else {
                logger.warn(
                  "Couldn't find a reason for clip production failure",
                  paramsToAnalyze,
                );
              }
            }
            bumpRetryCount();
          } else {
            // Notify user if results were outside the requested duration range
            if (matchedParts.some((clip: any) => clip.durationRelaxed)) {
              videoCreator.showToast({
                state: 'warning',
                message:
                  'No clips matched the exact duration. Showing the closest results.',
                delay: 7000,
              });
            }
            setProducedClips(matchedParts);
            break;
          }
        } catch (err: any) {
          if (err.message === 'Aborted') {
            logger.warn('Clip production was aborted', extractErrorInfo(err));
            return;
          }
          logger.error(
            'Error occurred while producing clips',
            extractErrorInfo(err),
          );
          setRetryCount(prev => prev + 1);
        }
      }

      // If we've exhausted all retries and still have no clips, fail the workflow
      if (retryCountRef.current >= MAX_RETRIES && monitoring.isProducing) {
        monitoring.failProduction(
          retryCountRef.current,
          MAX_RETRIES,
          'No clips produced after maximum retries',
          {
            theme: selectedPresets[0] || 'Custom',
            platform: selectedSocials[0] || undefined,
          },
        );
        EventTracker.trackClipGenerationFailed({
          story_id: videoCreator.story?.id,
          story_title: videoCreator.story?.title,
          video_id: videoCreator.currentVideo?.id,
          retry_count: retryCountRef.current,
          max_retries: MAX_RETRIES,
          error_message: 'No clips produced after maximum retries',
          theme: selectedPresets[0] || 'Custom',
          platform: selectedSocials[0] || undefined,
        });
      }

      producerAbortController.current = null;
      setRetryCount(0);
      setIsProducing(false);
    };

    const notifyUserAboutTheLackOfClips = ({
      reason,
    }: {
      reason: string;
    }): void => {
      if (!reason) return;

      videoCreator.showToast({
        state: 'error',
        message: reason,
        delay: 7000,
      });
    };

    return (
      <ClipProducerUI
        supportedPresets={props.supportedPresets || []}
        isProducing={isProducing}
        retryCount={retryCount}
        onProductionCancel={handleProductionCancel}
        selectedDuration={selectedDuration}
        setSelectedDuration={setSelectedDuration}
        selectedPresets={selectedPresets}
        setSelectedPresets={setSelectedPresets}
        onPresetClick={onPresetClick}
        customPresetText={presetInputValue}
        setCustomPresetText={setPresetInputValue}
        selectedSocials={selectedSocials}
        setSelectedSocials={setSelectedSocials}
        producedClips={producedClips}
        showModal={showModal}
        isProducingDisable={isProducingDisabled()}
        onProduceButtonClick={onProduceButtonClick}
        closeProducingModal={closeProducingModal}
        // Suggested topics (clip themes)
        suggestedThemes={suggestedThemes}
        selectedSuggestedTheme={selectedSuggestedTheme}
        onSuggestedThemeClick={onSuggestedThemeClick}
        // New speaker selection props
        speakers={speakers}
        selectedSpeakers={selectedSpeakers}
        setSelectedSpeakers={setSelectedSpeakers}
        useSpeakerFilter={useSpeakerFilter}
        setUseSpeakerFilter={setUseSpeakerFilter}
        isLoadingSpeakers={isLoadingSpeakers}
        showSpeakerSelector={showSpeakerSelector}
        // Goal selection props
        selectedGoal={selectedGoal}
        setSelectedGoal={setSelectedGoal}
        useGoalFilter={useGoalFilter}
        setUseGoalFilter={setUseGoalFilter}
        // Audience selection props
        selectedAudiences={selectedAudiences}
        setSelectedAudiences={setSelectedAudiences}
        useAudienceFilter={useAudienceFilter}
        setUseAudienceFilter={setUseAudienceFilter}
        // Video Template selection
        selectedVideoTemplateId={selectedVideoTemplateId}
        setSelectedVideoTemplateId={setSelectedVideoTemplateId}
        useVideoTemplateFilter={useVideoTemplateFilter}
        setUseVideoTemplateFilter={setUseVideoTemplateFilter}
        // Topic filter selection
        storyTopics={storyTopics}
        selectedTopics={selectedTopics}
        handleTopicsApply={handleTopicsApply}
        // Feature flag for custom preset input
        enableCustomPreset={contentStudioClipProducerEnableCustom}
      />
    );
  }),
  (oldProps, newProps) =>
    oldProps.supportedPresets?.length === newProps.supportedPresets?.length,
);

export default AIClipProducer;
